import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ButtonMode, ButtonSize, Dialog } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { IBenfitTypeWithMaxAmount } from '@epi-models/containers/ManageBenefitGroups';
import { LimitType, LUNCH } from '@epi-constants/benefitTypes';
import { IAvailableBenefits } from '@epi-models/containers/ManageBenefits';
import Paper from '@mui/material/Paper';

import BenefitSettingsLunch from './BenefitSettingsLunch';
import BenefitSettingsItem from './BenefitSettingsItem';

type OwnProps = {
  showInformationModal: boolean;
  closeModal: (isSuccess?: boolean) => void;
  selectedBenefits: IAvailableBenefits[];
  benefitMaxAmounts: IBenfitTypeWithMaxAmount;
  isSuccess: boolean;
  isError: boolean;
  resetMutation: () => void;
};

export const BenefitSettingsModal = ({
  showInformationModal,
  closeModal,
  selectedBenefits,
  benefitMaxAmounts,
  isSuccess,
  isError,
  resetMutation
}: OwnProps) => {
  const { t } = useTranslation();
  const {
    sizeMedium,
    sizeExtraExtraLarge,
    gray50,
    gray85,
    fontSemiBold,
    fontRegularBold
  } = useTheme();

  const createNativeGroupName = (groupName: string) => {
    const nativeName = t(
      `components.manage_benefits.new.benefit_group_names.${groupName.toLowerCase()}`
    );

    return `${nativeName}Group`;
  };
  const { reset: formReset, formState } = useFormContext();

  useEffect(() => {
    if (isSuccess) {
      closeModal(isSuccess);
      resetMutation();
    } else if (isError) {
      closeModal(!isSuccess);
      resetMutation();
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    if (showInformationModal) {
      formReset({
        groups: selectedBenefits.map(b => ({
          benefitType: b.type,
          maxAmountPerPeriod:
            b.type === LUNCH
              ? benefitMaxAmounts[b.type][0].toFixed(2)
              : benefitMaxAmounts[b.type][0],
          isMaximumSpendingLimit: LimitType.maximum,
          groupId: b.id,
          groupName: createNativeGroupName(b.type),
          fundsLoadingType: undefined,
          recurringTopupRequestEnabled: false
        }))
      });
    }
  }, [showInformationModal]);

  return (
    <Dialog
      open={showInformationModal}
      onClose={() => closeModal()}
      PaperComponent={Paper}
      disablePortal
    >
      <Dialog.Title>
        <Box
          fontSize={sizeExtraExtraLarge}
          fontWeight={fontSemiBold}
          color={gray85}
        >
          {t('components.manage_benefit_modal.title')}
        </Box>
        <Box
          mt={1}
          fontSize={sizeMedium}
          fontWeight={fontRegularBold}
          color={gray50}
        >
          {t('components.manage_benefit_modal.subtitle')}
        </Box>
      </Dialog.Title>
      <Dialog.Content>
        <Box display="flex" flexDirection="column" gap={4}>
          {selectedBenefits.some(s => s.type === LUNCH) && (
            <BenefitSettingsLunch
              maxAmounts={benefitMaxAmounts[LUNCH]}
              index={selectedBenefits.findIndex(s => s.type === LUNCH)}
            />
          )}
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(420px, 1fr))"
            gap={3}
          >
            {selectedBenefits
              .filter(benefit => benefit.type !== LUNCH)
              .map(benefit => {
                return (
                  <BenefitSettingsItem
                    benefit={benefit}
                    maxAmounts={benefitMaxAmounts[benefit.type]}
                    key={benefit.id}
                    index={selectedBenefits.findIndex(
                      s => s.type === benefit.type
                    )}
                  />
                );
              })}
          </Box>
        </Box>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          id="cancel-benefit-settings"
          onClick={() => closeModal()}
          mode={ButtonMode.Secondary}
          size={ButtonSize.Medium}
          style={{ border: 'none', boxShadow: 'none' }}
          type="button"
        >
          {t(`components.manage_benefit_lunch.detail_action_cancel`)}
        </Button>
        <Button
          id="save-benefit-settings"
          mode={ButtonMode.Primary}
          size={ButtonSize.Medium}
          type="submit"
          disabled={!formState.isValid}
        >
          {t(`components.manage_benefit_lunch.detail_action_save`)}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
